import type { AnchorHTMLAttributes, ReactNode } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type {
  InvalidNickname,
  SignUp as SignUpRequest,
  Sport,
} from '@sorare/wallet-shared';

import type { Data } from '#src/components/Form/index.tsx';
import Form from '#src/components/Form/index.tsx';
import { NewOAuth, OAuthMethods } from '#src/components/NewOAuth.tsx';
import { EmailLogo } from '#src/components/icons/Email.tsx';
import { Partner, PartnersList } from '#src/forms/PartnersList/index.tsx';
import type { Props } from '#src/forms/types.ts';
import useCreateWallet from '#src/hooks/useCreateWallet.ts';
import useHasher from '#src/hooks/useHasher.ts';
import useInfo from '#src/hooks/useInfo.ts';
import useRequestPlaceholderResize from '#src/hooks/useRequestPlaceholderResize.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { validate } from '#src/lib/password.ts';

const fields = [
  ['email', 'email', { required: true }],
  ['password', 'password', { required: true, autoComplete: 'new-password' }],
] as const;

type Field = (typeof fields)[number][0];

type NonMobileProps = Props & { isAndroidApp: boolean };

type MobileProps = Props & { switchToSignIn?: never } & {
  isAndroidApp: boolean;
};

type FormProps = NonMobileProps | MobileProps;

type FinalProps = {
  nicknameReceived: string;
  sportSelected: Sport;
} & FormProps;

const buildAnchorParameters = (
  url: string,
  useDeepLink: boolean
): AnchorHTMLAttributes<unknown> => {
  if (useDeepLink) {
    return {
      href: `sorare://external-link?url=${encodeURIComponent(url)}`,
    };
  }
  return {
    href: url,
    target: '_blank',
    rel: 'noreferrer',
  };
};

const resolveTermsAndConditionsAndPolicyLinks = (
  src: string,
  useDeepLink: boolean
): (string | ReactNode)[] => {
  const matchers =
    /<terms>(?<terms>[^<]+)<\/terms>|<privacy>(?<privacy>[^<]+)<\/privacy>/g;
  const result = [];
  let match = matchers.exec(src);
  let previousIndex = 0;
  let linkCounter = 0;
  while (match) {
    if (previousIndex < match.index) {
      result.push(src.substring(previousIndex, match.index));
    }
    if (match.groups) {
      if (match.groups.terms) {
        result.push(
          <a
            key={`generated-link-${linkCounter}`}
            {...buildAnchorParameters(
              'https://sorare.com/terms_and_conditions',
              useDeepLink
            )}
          >
            {match.groups.terms}
          </a>
        );
        linkCounter += 1;
      }
      if (match.groups.privacy) {
        result.push(
          <a
            key={`generated-link-${linkCounter}`}
            {...buildAnchorParameters(
              'https://sorare.com/privacy_policy',
              useDeepLink
            )}
          >
            {match.groups.privacy}
          </a>
        );
        linkCounter += 1;
      }
    }
    previousIndex = matchers.lastIndex;
    match = matchers.exec(src);
  }
  result.push(src.substring(previousIndex));
  return result;
};

export const SignUp = ({
  nicknameReceived,
  sportSelected,
  isAndroidApp,
  onSuccess,
}: FinalProps) => {
  const createWallet = useCreateWallet();
  const hasher = useHasher();
  const [displayFields, setDisplayFields] = useState(false);
  const { sendRequest } = useContext(MessagingContext)!;

  const onResizeOrMove = useRequestPlaceholderResize();

  const { dict } = useInfo();

  const oAuthRef = useRef<HTMLDivElement>(null);
  const oAuthMethods = isAndroidApp
    ? [OAuthMethods.GOOGLE]
    : [OAuthMethods.GOOGLE, OAuthMethods.APPLE];

  const effectiveOnResizeOrMove = useCallback(
    (dimension: DOMRectReadOnly) => {
      if (!oAuthRef.current) {
        return onResizeOrMove(dimension);
      }
      const oAuthDimension = oAuthRef.current.getBoundingClientRect();
      return onResizeOrMove({
        ...dimension,
        width: Math.max(dimension.width, oAuthDimension.width),
        height: dimension.height + oAuthDimension.height,
      });
    },
    [onResizeOrMove]
  );

  const [approvedTcsAndAgeLimit, toggleApprovedTcsAndAgeLimit] =
    useToggle(false);

  const [agreeToShareAll, setAgreeToShareAll] = useState<boolean>(false);
  const [agreeToShareSpecific, setAgreeToShareSpecific] = useState<Partner[]>(
    []
  );

  const [showPartnersList, setShowPartnersList] = useState(false);

  const [submittedWithNoApproval, setSubmittedWithNoApproval] = useState(false);

  const onEffectiveSubmit = useCallback(
    async ({ password, email }: Data<Field>) => {
      if (!approvedTcsAndAgeLimit) {
        setSubmittedWithNoApproval(true);
        return {};
      }
      const passwordError = await validate(email, nicknameReceived, password);
      if (passwordError) return { password: passwordError };

      const wallet = await createWallet(password, email);
      const passwordHash = await hasher(password);

      const signUpRequestArgs: SignUpRequest['request']['args'] = {
        sport: sportSelected,
        email,
        nickname: nicknameReceived,
        passwordHash,
        wallet,
        acceptTerms: approvedTcsAndAgeLimit,
        acceptAgeLimit: approvedTcsAndAgeLimit,
        acceptPrivacyPolicy: approvedTcsAndAgeLimit,
        agreedToReceiveOffersFromPartners: agreeToShareAll,
        agreedToReceiveOffersFromPartnersSpecific: agreeToShareSpecific,
        mobile: isAndroidApp,
      };

      const { error } = await sendRequest<SignUpRequest>(
        'signUp',
        signUpRequestArgs
      );

      if (error) return error;
      return undefined;
    },
    [
      approvedTcsAndAgeLimit,
      nicknameReceived,
      createWallet,
      hasher,
      sportSelected,
      agreeToShareAll,
      agreeToShareSpecific,
      isAndroidApp,
      sendRequest,
    ]
  );

  const onBack = useCallback(
    () => setShowPartnersList(false),
    [setShowPartnersList]
  );

  const iAgreeToTermsAndConditionsAndAgeLimit = useMemo<(string | ReactNode)[]>(
    () =>
      resolveTermsAndConditionsAndPolicyLinks(
        dict.iAgreeToTermsAndConditionsAndAgeLimit,
        false
      ),
    [dict.iAgreeToTermsAndConditionsAndAgeLimit]
  );

  const youMustApproveTcsAndAgeLimit =
    submittedWithNoApproval && !approvedTcsAndAgeLimit;

  const isPartiallyAgreeToShare = agreeToShareSpecific.length > 0;

  const handleChangeAgreeToShareAll = () => {
    const newAgreeToShareAll = !agreeToShareAll;
    if (newAgreeToShareAll) {
      setAgreeToShareAll(true);
      setAgreeToShareSpecific([Partner.EPL]);
    } else {
      setAgreeToShareAll(false);
      setAgreeToShareSpecific([]);
    }
  };

  const handlePartnersList = (partnersListInput: Partner[]) => {
    setAgreeToShareSpecific(partnersListInput);
    setShowPartnersList(false);
    setAgreeToShareAll(false);
  };

  const agreeToShareLabel = (src: string) => {
    const matchers = /<partnerslist>(?<partnerslist>[^<]+)<\/partnerslist>/g;
    const result = [];
    let match = matchers.exec(src);
    let previousIndex = 0;
    let linkCounter = 0;

    while (match) {
      if (previousIndex < match.index) {
        result.push(src.substring(previousIndex, match.index));
      }
      if (match.groups) {
        if (match.groups.partnerslist) {
          result.push(
            <button
              className="inline-button"
              type="button"
              key={`generated-link-${linkCounter}`}
              onClick={() => setShowPartnersList(true)}
            >
              {match.groups.partnerslist}
            </button>
          );
          linkCounter += 1;
        }
      }
      previousIndex = matchers.lastIndex;
      match = matchers.exec(src);
    }
    result.push(src.substring(previousIndex));
    return result;
  };

  useEffect(() => {
    const element = document.getElementsByClassName('dialog')[0];
    document.body?.classList.add('signUpBodyOverrides');
    element?.classList.add('signUpBodyOverrides');

    return () => {
      element?.classList.remove('signUpBodyOverrides');
      document.body?.classList.remove('signUpBodyOverrides');
    };
  });

  return (
    <Form
      inputs={displayFields ? fields : []}
      onSubmit={async args => {
        if (!approvedTcsAndAgeLimit) {
          setSubmittedWithNoApproval(true);
          return {};
        }

        if (displayFields) {
          const errors = await onEffectiveSubmit(args);
          if (errors?.nickname) {
            sendRequest<InvalidNickname>('invalidNickname', {
              error: errors.nickname,
            });
          }
          return errors;
        }
        setDisplayFields(true);
        return {};
      }}
      onSuccess={onSuccess}
      submitLabel={
        displayFields ? (
          dict.signUp
        ) : (
          <div className="signUpWithEmail">
            <EmailLogo />
            {dict.signUpEmail}
          </div>
        )
      }
      onResizeOrMove={effectiveOnResizeOrMove}
      additionalScreen={
        !!showPartnersList && (
          <PartnersList
            onBack={onBack}
            onSubmit={handlePartnersList}
            agreeToShare={agreeToShareAll}
            agreeToShareSpecific={agreeToShareSpecific}
          />
        )
      }
      forceDisabled={!approvedTcsAndAgeLimit}
    >
      <>
        {!displayFields && (
          <div className="signUp additionalFieldsWrapper">
            <div className="signUp additionalFields">
              <h1>{dict.required}</h1>
              <div>
                <label
                  htmlFor="approveTcs"
                  className={youMustApproveTcsAndAgeLimit ? 'error' : ''}
                >
                  <input
                    id="approveTcs"
                    type="checkbox"
                    checked={approvedTcsAndAgeLimit}
                    onChange={toggleApprovedTcsAndAgeLimit}
                  />
                  <span>{iAgreeToTermsAndConditionsAndAgeLimit}</span>
                </label>
                {!!youMustApproveTcsAndAgeLimit && (
                  <p className="error">
                    {dict.youMustAcceptTermsAndConditions}
                  </p>
                )}
              </div>
            </div>
            <div className="signUp additionalFields">
              <h1>{dict.optional}</h1>
              <div>
                <label htmlFor="agreeToShare">
                  <input
                    type="checkbox"
                    id="agreeToShare"
                    checked={agreeToShareAll || isPartiallyAgreeToShare}
                    onChange={handleChangeAgreeToShareAll}
                    {...(!agreeToShareAll && { className: 'partially' })}
                  />
                  <span>{agreeToShareLabel(dict.iAgreeToShareV2)}</span>
                </label>
              </div>
            </div>
          </div>
        )}
        {!displayFields && (
          <>
            <NewOAuth
              methods={oAuthMethods}
              nickname={nicknameReceived}
              sport={sportSelected}
              acceptTerms={approvedTcsAndAgeLimit}
              acceptAgeLimit={approvedTcsAndAgeLimit}
              acceptToShareAll={agreeToShareAll}
              acceptToShareSpecific={agreeToShareSpecific}
              forceDisabled={!approvedTcsAndAgeLimit}
            />
            <div className="OAuthSeparator">
              <span />
              {dict.oAuthSeparator}
              <span />
            </div>
          </>
        )}
      </>
    </Form>
  );
};
